/**
 * DTTS Events – Calendar & List View
 * Reads event data from a JSON blob rendered by Liquid,
 * then handles filtering, view toggling, calendar rendering, and .ics export.
 *
 * Each event has: start (ISO datetime), end (ISO datetime, optional),
 * title, category, location, description, image, featured, details_url.
 */
(function () {
  'use strict';

  const DAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const CATEGORY_SLUG = (cat) => (cat || '').toLowerCase().replace(/\s+/g, '-');

  /* Brand palette colors for category dots (excludes white) */
  const CATEGORY_PALETTE = [
    '#FF8200', /* Orange  */
    '#041E42', /* Navy    */
    '#4C9FC8', /* Blue    */
    '#F5E1A4', /* Gold    */
    '#BA0C2F', /* Red     */
    '#E24585', /* Hot Pink */
    '#D0858B', /* Dusty Rose */
    '#F3CFB3', /* Peach   */
    '#EAA09C', /* Salmon  */
    '#E89E9D', /* Rose    */
    '#2D2926', /* Black   */
    '#373A36', /* Dark Gray */
    '#D0D0CE', /* Light Gray */
  ];
  const CATEGORY_OVERRIDES = {
    'live-music': '#BA0C2F',
    'holiday': '#4C9FC8',
    'holiday-events': '#4C9FC8',
    'special-event': '#F5E1A4',
    'special-events': '#F5E1A4',
  };
  const categoryColorMap = {};
  var nextColorIndex = 0;

  function getCategoryColor(cat) {
    var slug = CATEGORY_SLUG(cat);
    if (!slug) return CATEGORY_PALETTE[0];
    if (CATEGORY_OVERRIDES[slug]) return CATEGORY_OVERRIDES[slug];
    if (!categoryColorMap[slug]) {
      categoryColorMap[slug] = CATEGORY_PALETTE[nextColorIndex % CATEGORY_PALETTE.length];
      nextColorIndex++;
    }
    return categoryColorMap[slug];
  }

  /* ------------------------------------------------
     Date/time helpers – parse ISO start/end strings
     ------------------------------------------------ */
  function parseEvent(ev) {
    var d = new Date(ev.start);
    if (isNaN(d.getTime())) {
      ev._invalid = true;
      ev._startDate = new Date(0);
      ev._dateKey = '';
      ev._timeStr = '';
      return;
    }
    ev._startDate = d;
    ev._dateKey = d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
    ev._timeStr = formatTime(d);
    if (ev.end) {
      var ed = new Date(ev.end);
      ev._endDate = ed;
      ev._endTimeStr = formatTime(ed);
    }
  }

  function formatTime(d) {
    var h = d.getHours();
    var m = d.getMinutes();
    var ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + String(m).padStart(2, '0') + ' ' + ampm;
  }

  function toICSDate(d) {
    return d.getFullYear() +
      String(d.getMonth() + 1).padStart(2, '0') +
      String(d.getDate()).padStart(2, '0') + 'T' +
      String(d.getHours()).padStart(2, '0') +
      String(d.getMinutes()).padStart(2, '0') +
      String(d.getSeconds()).padStart(2, '0');
  }

  /* ------------------------------------------------
     Recurrence – expand recurring events into instances
     ------------------------------------------------ */
  var DAY_MAP = { 'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4, 'friday': 5, 'saturday': 6 };

  function expandRecurringEvents(events) {
    var expanded = [];
    var horizon = new Date();
    horizon.setMonth(horizon.getMonth() + 6);

    events.forEach(function (ev) {
      expanded.push(ev);
      var rule = (ev.recurrence || '').toLowerCase().trim();
      if (!rule || rule === 'none') return;

      var start = new Date(ev.start);
      if (isNaN(start.getTime())) return;

      var endDate = ev.recurrence_end ? new Date(ev.recurrence_end) : null;
      if (endDate && isNaN(endDate.getTime())) endDate = null;
      var limit = endDate && endDate < horizon ? endDate : horizon;

      var duration = 0;
      if (ev.end) {
        var orig_end = new Date(ev.end);
        if (!isNaN(orig_end.getTime())) duration = orig_end - start;
      }

      /* Parse recurring_days (e.g. "wednesday,thursday") */
      var recurringDays = [];
      if (ev.recurring_days) {
        ev.recurring_days.split(',').forEach(function(d) {
          var day = d.trim().toLowerCase();
          if (DAY_MAP.hasOwnProperty(day)) recurringDays.push(DAY_MAP[day]);
        });
      }

      if (recurringDays.length > 0 && rule === 'weekly') {
        /* Multi-day weekly: generate instances for each specified day each week */
        var weekStart = new Date(start);
        /* Move to the start of the week (Sunday) */
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());

        /* Advance one week to avoid duplicating the original event's exact date */
        var firstWeek = new Date(weekStart);

        while (weekStart <= limit) {
          recurringDays.forEach(function(dayNum) {
            var instanceDate = new Date(weekStart);
            instanceDate.setDate(weekStart.getDate() + dayNum);
            instanceDate.setHours(start.getHours(), start.getMinutes(), start.getSeconds());

            /* Skip if this is the original event's exact date or before it */
            if (instanceDate.getTime() === start.getTime()) return;
            if (instanceDate < start) return;
            if (instanceDate > limit) return;

            var instance = {};
            for (var key in ev) {
              if (ev.hasOwnProperty(key) && key.charAt(0) !== '_') {
                instance[key] = ev[key];
              }
            }
            instance.start = toISO(instanceDate);
            if (duration > 0) {
              instance.end = toISO(new Date(instanceDate.getTime() + duration));
            }
            instance._recurring = true;
            instance._sourceTitle = ev.title;
            expanded.push(instance);
          });

          weekStart.setDate(weekStart.getDate() + 7);
        }
      } else {
        /* Standard recurrence (original behavior) */
        var cursor = new Date(start);
        advanceCursor(cursor, rule);

        while (cursor <= limit) {
          var instance = {};
          for (var key in ev) {
            if (ev.hasOwnProperty(key) && key.charAt(0) !== '_') {
              instance[key] = ev[key];
            }
          }
          instance.start = toISO(cursor);
          if (duration > 0) {
            instance.end = toISO(new Date(cursor.getTime() + duration));
          }
          instance._recurring = true;
          instance._sourceTitle = ev.title;
          expanded.push(instance);

          advanceCursor(cursor, rule);
        }
      }
    });

    return expanded;
  }

  function advanceCursor(cursor, rule) {
    switch (rule) {
      case 'daily':
        cursor.setDate(cursor.getDate() + 1);
        break;
      case 'weekly':
        cursor.setDate(cursor.getDate() + 7);
        break;
      case 'bi weekly':
        cursor.setDate(cursor.getDate() + 14);
        break;
      case 'monthly':
        cursor.setMonth(cursor.getMonth() + 1);
        break;
    }
  }

  function toISO(d) {
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0') + 'T' +
      String(d.getHours()).padStart(2, '0') + ':' +
      String(d.getMinutes()).padStart(2, '0') + ':' +
      String(d.getSeconds()).padStart(2, '0');
  }

  /* ------------------------------------------------
     Init – called once per section on the page
     ------------------------------------------------ */
  function init(section) {
    const dataEl = section.querySelector('[data-dtts-events-json]');
    if (!dataEl) return;

    let events;
    try {
      events = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('DTTS Events: failed to parse event JSON', e);
      return;
    }

    /* Expand recurring events into individual instances */
    events = expandRecurringEvents(events);

    /* Parse dates and sort by start ascending */
    events.forEach(parseEvent);
    events.sort((a, b) => a._startDate - b._startDate);

    /* Assign indices AFTER sorting so they match positions in state.events */
    events.forEach(function (ev, i) { ev._index = i; });

    const pageSize = parseInt(section.querySelector('.dtts-events').dataset.listPageSize, 10) || 10;

    const savedView = localStorage.getItem('dtts-events-view') || 'list';

    const state = {
      events: events,
      activeFilter: 'all',
      activeView: savedView,
      calendarDate: new Date(),
      selectedCalendarDate: null,
      listPageSize: pageSize,
      listVisible: pageSize,
    };

    buildCategoryFilters(section, state);
    bindViewToggle(section, state);
    renderFeaturedEvent(section, state);
    renderListView(section, state);
    renderCalendarView(section, state);

    /* Set the correct toggle button as active based on saved view */
    var toggleBtns = section.querySelectorAll('.dtts-events__view-btn');
    toggleBtns.forEach(function(btn) {
      btn.classList.toggle('is-active', btn.dataset.view === savedView);
    });

    showView(section, state.activeView);
  }

  /* ------------------------------------------------
     Category Filters
     ------------------------------------------------ */
  function getCategories(events) {
    const counts = {};
    events.forEach((e) => {
      const cat = e.category || 'Uncategorized';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }

  function buildCategoryFilters(section, state) {
    const container = section.querySelector('.dtts-events__filters');
    if (!container) return;

    /* Only build filters from upcoming events (matches the list view), so a
       category whose events have all passed doesn't get a filter button. */
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = state.events.filter((e) => !e._invalid && e._startDate >= today);

    const counts = getCategories(upcoming);
    container.innerHTML = '';

    /* "All Events" button */
    const allBtn = document.createElement('button');
    allBtn.className = 'dtts-events__filter-btn is-active';
    allBtn.dataset.filter = 'all';
    allBtn.textContent = 'All Events (' + upcoming.length + ')';
    container.appendChild(allBtn);

    Object.keys(counts).forEach((cat) => {
      const btn = document.createElement('button');
      btn.className = 'dtts-events__filter-btn';
      btn.dataset.filter = cat;
      var slug = CATEGORY_SLUG(cat);
      btn.innerHTML = '<span class="dtts-events__category-dot dtts-events__category-dot--' + slug + '" style="background:' + getCategoryColor(cat) + '"></span> ' + escHTML(cat) + ' (' + counts[cat] + ')';
      container.appendChild(btn);
    });

    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.dtts-events__filter-btn');
      if (!btn) return;
      container.querySelectorAll('.dtts-events__filter-btn').forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      state.activeFilter = btn.dataset.filter;
      renderListView(section, state);
      renderCalendarView(section, state);
    });
  }

  /* ------------------------------------------------
     View Toggle
     ------------------------------------------------ */
  function bindViewToggle(section, state) {
    var toggle = section.querySelector('.dtts-events__view-toggle');
    var btns = section.querySelectorAll('.dtts-events__view-btn');
    if (!toggle || !btns.length) return;

    /* Create slider element */
    var slider = document.createElement('div');
    slider.className = 'dtts-events__view-slider';
    slider.innerHTML = '&nbsp;';
    toggle.appendChild(slider);

    function moveSlider() {
      var activeBtn = toggle.querySelector('.dtts-events__view-btn.is-active');
      if (activeBtn) {
        slider.style.left = activeBtn.offsetLeft + 'px';
        slider.style.width = activeBtn.offsetWidth + 'px';
      }
    }

    /* Initial position (no transition, double rAF to ensure layout is complete) */
    slider.style.transition = 'none';
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        moveSlider();
        /* Re-enable transition after positioning */
        setTimeout(function() {
          slider.style.transition = '';
        }, 50);
      });
    });

    btns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        btns.forEach(function(b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        state.activeView = btn.dataset.view;
        localStorage.setItem('dtts-events-view', state.activeView);
        moveSlider();
        showView(section, state.activeView);
      });
    });
  }

  function showView(section, view) {
    const listView = section.querySelector('.dtts-events__list-view');
    const calView = section.querySelector('.dtts-events__calendar-view');
    if (listView) listView.classList.toggle('is-active', view === 'list');
    if (calView) calView.classList.toggle('is-active', view === 'calendar');
  }

  /* ------------------------------------------------
     Filtered events helper
     ------------------------------------------------ */
  function filteredEvents(state) {
    if (state.activeFilter === 'all') return state.events;
    return state.events.filter((e) => e.category === state.activeFilter);
  }

  /* ------------------------------------------------
     List View
     ------------------------------------------------ */
  function renderFeaturedEvent(section, state) {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var featuredEvents = [];

    var seenTitles = {};
    state.events.forEach(function(ev) {
      if (ev._invalid) return;
      if (ev._startDate < today) return;
      if (!ev.featured) return;
      /* Deduplicate by title — show only one card per unique event */
      var key = (ev.title || '').toLowerCase().trim();
      if (seenTitles[key]) return;
      seenTitles[key] = true;
      featuredEvents.push(ev);
    });

    var featuredWrap = section.querySelector('.dtts-events__featured-wrap');
    if (!featuredWrap) return;

    /* Hide the entire featured section (background, padding, label and
       all) when there are no featured events — not just the inner wrap. */
    var featuredSection = section.querySelector('.dtts-events__featured-section');

    if (featuredEvents.length === 0) {
      /* No featured events: let the upcoming section keep its normal top
         padding (the padding-0 rule is scoped to this class). */
      section.classList.remove('dtts-events--has-featured');
      if (featuredSection) featuredSection.style.display = 'none';
      featuredWrap.style.display = 'none';
      return;
    }

    section.classList.add('dtts-events--has-featured');
    if (featuredSection) featuredSection.style.display = '';
    featuredWrap.style.display = '';
    var carouselEnabled = section.querySelector('.dtts-events').dataset.featuredCarousel === 'true';

    if (featuredEvents.length === 1 || !carouselEnabled) {
      featuredWrap.innerHTML = renderFeaturedCardInner(featuredEvents[0]);
    } else {
      var currentSlide = 0;
      var eventsEl = section.querySelector('.dtts-events');
      var autoplay = eventsEl.dataset.featuredAutoplay === 'true';
      var speed = (parseInt(eventsEl.dataset.featuredSpeed, 10) || 5) * 1000;
      var pauseIconSVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>';
      var playIconSVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
      var html = '<div class="dtts-events__featured-carousel">' +
          '<button class="dtts-events__featured-nav dtts-events__featured-prev" aria-label="Previous featured event">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M15 18l-6-6 6-6"/></svg>' +
          '</button>' +
          '<div class="dtts-events__featured-slides">' +
            '<div class="dtts-events__featured-track">';

      featuredEvents.forEach(function(ev, i) {
        html += '<div class="dtts-events__featured-slide' + (i === 0 ? ' is-active' : '') + '">' +
          renderFeaturedCardInner(ev) +
        '</div>';
      });

      /* Append a clone of slide 0 at the end so forward wrap (last -> first)
         can keep translating leftward — eliminates the visible "bounce"
         that happens with 2 slides when going N-1 -> 0 would otherwise
         slide right back. We animate to the clone, then on transitionend
         snap the track back to slide 0 with no transition. */
      html += '<div class="dtts-events__featured-slide dtts-events__featured-slide--clone" aria-hidden="true">' +
        renderFeaturedCardInner(featuredEvents[0]) +
      '</div>';

      html += '</div></div>' +
          '<button class="dtts-events__featured-nav dtts-events__featured-next" aria-label="Next featured event">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M9 18l6-6-6-6"/></svg>' +
          '</button>' +
          '<div class="dtts-events__featured-dots">';

      featuredEvents.forEach(function(ev, i) {
        html += '<button class="dtts-events__featured-dot' + (i === 0 ? ' is-active' : '') + '" data-slide="' + i + '"></button>';
      });

      if (autoplay) {
        html += '<button type="button" class="dtts-events__featured-pause" data-featured-pause aria-label="Pause automatic slideshow" aria-pressed="false">' + pauseIconSVG + '</button>';
      }

      html += '</div></div>';
      featuredWrap.innerHTML = html;

      var track = featuredWrap.querySelector('.dtts-events__featured-track');
      var slides = featuredWrap.querySelectorAll('.dtts-events__featured-slide:not(.dtts-events__featured-slide--clone)');
      var dots = featuredWrap.querySelectorAll('.dtts-events__featured-dot');
      var slideCount = featuredEvents.length;
      var isAnimating = false;

      function goToSlide(idx) {
        if (isAnimating) return;
        var prevIdx = currentSlide;
        slides.forEach(function(s) { s.classList.remove('is-active'); });
        dots.forEach(function(d) { d.classList.remove('is-active'); });
        currentSlide = idx;
        slides[currentSlide].classList.add('is-active');
        dots[currentSlide].classList.add('is-active');
        if (!track) return;

        if (idx === 0 && prevIdx === slideCount - 1) {
          /* Forward wrap: animate past the last real slide into the clone,
             then snap back to slide 0 without transition. */
          track.style.transition = '';
          track.style.transform = 'translateX(' + (-slideCount * 100) + '%)';
          isAnimating = true;
          var onEnd = function() {
            track.removeEventListener('transitionend', onEnd);
            track.style.transition = 'none';
            track.style.transform = 'translateX(0)';
            // Force reflow so the next style change re-enables the transition.
            void track.offsetHeight;
            track.style.transition = '';
            isAnimating = false;
          };
          track.addEventListener('transitionend', onEnd);
        } else {
          track.style.transition = '';
          track.style.transform = 'translateX(' + (-idx * 100) + '%)';
        }
      }

      featuredWrap.querySelector('.dtts-events__featured-prev').addEventListener('click', function() {
        goToSlide((currentSlide - 1 + featuredEvents.length) % featuredEvents.length);
        resetAutoplay();
      });

      featuredWrap.querySelector('.dtts-events__featured-next').addEventListener('click', function() {
        goToSlide((currentSlide + 1) % featuredEvents.length);
        resetAutoplay();
      });

      dots.forEach(function(dot) {
        dot.addEventListener('click', function() {
          goToSlide(parseInt(dot.dataset.slide, 10));
          resetAutoplay();
        });
      });

      /* Auto-rotate */
      var autoplayTimer = null;
      var userPaused = false;

      function startAutoplay() {
        if (!autoplay || userPaused) return;
        autoplayTimer = setInterval(function() {
          goToSlide((currentSlide + 1) % featuredEvents.length);
        }, speed);
      }

      function resetAutoplay() {
        if (autoplayTimer) clearInterval(autoplayTimer);
        startAutoplay();
      }

      /* Pause/play toggle (lets users stop autoplay, e.g. on touch where
         there's no hover-pause). */
      var pauseBtn = featuredWrap.querySelector('[data-featured-pause]');
      if (pauseBtn) {
        pauseBtn.addEventListener('click', function() {
          userPaused = !userPaused;
          if (userPaused) {
            if (autoplayTimer) clearInterval(autoplayTimer);
            pauseBtn.innerHTML = playIconSVG;
            pauseBtn.setAttribute('aria-label', 'Resume automatic slideshow');
            pauseBtn.setAttribute('aria-pressed', 'true');
          } else {
            pauseBtn.innerHTML = pauseIconSVG;
            pauseBtn.setAttribute('aria-label', 'Pause automatic slideshow');
            pauseBtn.setAttribute('aria-pressed', 'false');
            startAutoplay();
          }
        });
      }

      /* Pause on hover (desktop). Respects an explicit user pause. */
      featuredWrap.addEventListener('mouseenter', function() {
        if (autoplayTimer) clearInterval(autoplayTimer);
      });
      featuredWrap.addEventListener('mouseleave', function() {
        startAutoplay();
      });

      startAutoplay();
    }

    /* Apply image height class */
    var imageHeight = section.querySelector('.dtts-events').dataset.featuredImageHeight || 'medium';
    featuredWrap.querySelectorAll('.dtts-events__featured-image').forEach(function(img) {
      img.classList.add('dtts-events__featured-image--' + imageHeight);
    });

    bindCalendarDropdowns(section, state);
    bindDetailsButtons(section, state);
  }

  function renderListView(section, state) {
    const fEvents = filteredEvents(state);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming = [];
    fEvents.forEach((ev) => {
      if (ev._invalid) return;
      if (ev._startDate < today) return;
      upcoming.push(ev);
    });

    /* Upcoming list */
    const listEl = section.querySelector('.dtts-events__list');
    if (!listEl) return;

    const listEvents = upcoming;
    const viewMoreWrap = section.querySelector('.dtts-events__view-more-wrap');

    if (listEvents.length === 0) {
      listEl.innerHTML = '<div class="dtts-events__empty">No upcoming events found.</div>';
      if (viewMoreWrap) viewMoreWrap.style.display = 'none';
      return;
    }

    /* Reset visible count on filter change */
    state.listVisible = state.listPageSize;

    function renderPage(animateFrom) {
      var visible = listEvents.slice(0, state.listVisible);
      listEl.innerHTML = visible.map(renderListItem).join('');

      /* Stagger-fade newly revealed rows in (only those at index
         animateFrom and beyond) so View More slides them in instead of
         popping. Earlier rows are left untouched so they don't re-animate. */
      if (typeof animateFrom === 'number') {
        var items = listEl.querySelectorAll('.dtts-events__list-item');
        for (var i = animateFrom; i < items.length; i++) {
          var item = items[i];
          item.classList.add('dtts-events__list-item--enter');
          item.style.animationDelay = ((i - animateFrom) * 60) + 'ms';
        }
      }

      /* Bind calendar dropdowns */
      bindCalendarDropdowns(section, state);

      /* Bind details buttons */
      bindDetailsButtons(section, state);

      /* Show/hide View More button */
      if (viewMoreWrap) {
        if (state.listVisible < listEvents.length) {
          viewMoreWrap.style.display = '';
        } else {
          viewMoreWrap.style.display = 'none';
        }
      }
    }

    renderPage();

    /* Bind View More button */
    if (viewMoreWrap) {
      var viewMoreBtn = viewMoreWrap.querySelector('.dtts-events__view-more-btn');
      if (viewMoreBtn) {
        viewMoreBtn.onclick = function () {
          var prevVisible = state.listVisible;
          state.listVisible += state.listPageSize;
          renderPage(prevVisible);
        };
      }
    }
  }

  function renderFeaturedCardInner(ev) {
    var d = ev._startDate;
    var dateStr = DAYS_SHORT[d.getDay()] + ', ' +
      MONTHS[d.getMonth()].substring(0, 3).toUpperCase() + ' ' + d.getDate();
    var timeStr = ev._timeStr;
    if (ev._endTimeStr) timeStr += ' – ' + ev._endTimeStr;

    return '' +
      '<div class="dtts-events__featured">' +
        '<div class="dtts-events__featured-image">' +
          (ev.image ? '<img src="' + ev.image + '" alt="' + escHTML(ev.title) + '" loading="lazy"' + imagePositionStyle(ev.image_position) + '>' : 'Featured Event Image') +
        '</div>' +
        '<div class="dtts-events__featured-content">' +
          (ev.category ? '<span class="dtts-events__list-category"><span class="dtts-events__category-dot dtts-events__category-dot--' + CATEGORY_SLUG(ev.category) + '" style="background:' + getCategoryColor(ev.category) + '"></span> ' + escHTML(ev.category) + '</span>' : '') +
          '<h3 class="dtts-events__featured-title">' + escHTML(ev.title) + '</h3>' +
          (ev.description ? '<p class="dtts-events__featured-desc">' + escHTML(truncateWords(ev.description, 20)) + '</p>' : '') +
          '<div class="dtts-events__featured-meta">' +
            '<span class="dtts-events__meta-item">' + iconCalendar() + ' ' + dateStr + '</span>' +
            '<span class="dtts-events__meta-item">' + iconClock() + ' ' + timeStr + '</span>' +
          '</div>' +
          '<div class="dtts-events__featured-actions">' +
            '<button type="button" class="dtts-events__btn--details" data-details-toggle="' + ev._index + '">Details</button>' +
            calendarDropdownHTML(ev._index) +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function renderListItem(ev) {
    var d = ev._startDate;
    var monthShort = MONTHS[d.getMonth()].substring(0, 3);
    var dayNum = d.getDate();
    var dayShort = DAYS_SHORT[d.getDay()];
    var slug = CATEGORY_SLUG(ev.category);
    var idx = ev._index;

    return '' +
      '<div class="dtts-events__list-item" data-category="' + escHTML(ev.category || '') + '">' +
        '<div class="dtts-events__date-badge">' +
          '<span class="dtts-events__date-badge-month">' + monthShort + '</span>' +
          '<span class="dtts-events__date-badge-num">' + dayNum + '</span>' +
          '<span class="dtts-events__date-badge-day">' + dayShort + '</span>' +
        '</div>' +
        '<div class="dtts-events__list-info">' +
          '<div class="dtts-events__list-title">' + escHTML(ev.title) + '</div>' +
          '<div class="dtts-events__list-meta">' +
            (ev.category ? '<span class="dtts-events__list-category"><span class="dtts-events__category-dot dtts-events__category-dot--' + slug + '" style="background:' + getCategoryColor(ev.category) + '"></span> ' + escHTML(ev.category) + '</span>' : '') +
            '<span class="dtts-events__list-time">' + iconClock() + ' ' + escHTML(ev._timeStr) + (ev._endTimeStr ? ' – ' + escHTML(ev._endTimeStr) : '') + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="dtts-events__list-actions">' +
          '<button type="button" class="dtts-events__btn--details" data-details-toggle="' + idx + '">Details</button>' +
          calendarDropdownHTML(idx) +
        '</div>' +
      '</div>';
  }

  /* ------------------------------------------------
     Calendar View
     ------------------------------------------------ */
  function renderCalendarView(section, state) {
    const gridEl = section.querySelector('.dtts-events__calendar-grid');
    const monthEl = section.querySelector('.dtts-events__calendar-month');
    if (!gridEl || !monthEl) return;

    const year = state.calendarDate.getFullYear();
    const month = state.calendarDate.getMonth();
    monthEl.textContent = MONTHS[month] + ' ' + year;

    const fEvents = filteredEvents(state);

    /* Map events by YYYY-MM-DD key */
    const eventMap = {};
    fEvents.forEach((ev) => {
      var key = ev._dateKey;
      if (!eventMap[key]) eventMap[key] = [];
      eventMap[key].push(ev);
    });

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const todayStr = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    let html = '';
    /* Day headers */
    DAYS_SHORT.forEach((d) => {
      html += '<div class="dtts-events__calendar-day-header">' + d.charAt(0).toUpperCase() + d.slice(1).toLowerCase() + '</div>';
    });

    /* Previous month trailing days */
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      html += '<div class="dtts-events__calendar-cell is-outside"><span class="dtts-events__calendar-cell-num">' + d + '</span></div>';
    }

    /* Current month days */
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
      const isToday = dateStr === todayStr;
      const isSelected = state.selectedCalendarDate === dateStr;
      const dayEvents = eventMap[dateStr] || [];

      let cls = 'dtts-events__calendar-cell';
      if (isToday) cls += ' is-today';
      if (isSelected) cls += ' is-selected';

      html += '<div class="' + cls + '" data-date="' + dateStr + '">';
      html += '<span class="dtts-events__calendar-cell-num">' + d + '</span>';
      dayEvents.forEach((ev) => {
        const slug = CATEGORY_SLUG(ev.category);
        html += '<div class="dtts-events__calendar-cell-event">' +
          '<span class="dtts-events__category-dot dtts-events__category-dot--' + slug + '" style="background:' + getCategoryColor(ev.category) + '"></span>' +
          escHTML(ev.title) + '</div>';
      });
      html += '</div>';
    }

    /* Next month leading days */
    const totalCells = firstDay + daysInMonth;
    const remainder = totalCells % 7;
    if (remainder > 0) {
      for (let d = 1; d <= 7 - remainder; d++) {
        html += '<div class="dtts-events__calendar-cell is-outside"><span class="dtts-events__calendar-cell-num">' + d + '</span></div>';
      }
    }

    gridEl.innerHTML = html;

    /* Bind cell clicks */
    gridEl.querySelectorAll('.dtts-events__calendar-cell:not(.is-outside)').forEach((cell) => {
      cell.addEventListener('click', () => {
        state.selectedCalendarDate = cell.dataset.date;
        gridEl.querySelectorAll('.is-selected').forEach((c) => c.classList.remove('is-selected'));
        cell.classList.add('is-selected');
        renderSidebar(section, state, eventMap[cell.dataset.date] || [], cell.dataset.date);

        /* On mobile the sidebar sits below the calendar; bring its header
           into view so users notice their selection's events. Wait one
           frame for the sidebar to render its new content (and resolve its
           final height) before scrolling. Desktop already shows it
           alongside the grid, so this is mobile-only. */
        if (window.matchMedia('(max-width: 749px)').matches) {
          const sidebar = section.querySelector('.dtts-events__calendar-sidebar');
          if (sidebar) {
            requestAnimationFrame(() => {
              const rect = sidebar.getBoundingClientRect();
              const target = window.scrollY + rect.top - 16;
              window.scrollTo({ top: target, behavior: 'smooth' });
            });
          }
        }
      });
    });

    /* Bind nav */
    const prevBtn = section.querySelector('.dtts-events__calendar-prev');
    const nextBtn = section.querySelector('.dtts-events__calendar-next');
    const todayBtn = section.querySelector('.dtts-events__calendar-today-btn');

    if (prevBtn) {
      prevBtn.onclick = () => {
        state.calendarDate = new Date(year, month - 1, 1);
        state.selectedCalendarDate = null;
        renderCalendarView(section, state);
        clearSidebar(section);
      };
    }
    if (nextBtn) {
      nextBtn.onclick = () => {
        state.calendarDate = new Date(year, month + 1, 1);
        state.selectedCalendarDate = null;
        renderCalendarView(section, state);
        clearSidebar(section);
      };
    }
    if (todayBtn) {
      todayBtn.onclick = () => {
        state.calendarDate = new Date();
        state.selectedCalendarDate = todayStr;
        renderCalendarView(section, state);
        renderSidebar(section, state, eventMap[todayStr] || [], todayStr);
      };
    }

    renderLegend(section, state);
  }

  function renderSidebar(section, state, dayEvents, dateStr) {
    const header = section.querySelector('.dtts-events__sidebar-header');
    const body = section.querySelector('.dtts-events__sidebar-body');
    if (!header || !body) return;

    const d = new Date(dateStr + 'T00:00:00');
    header.textContent = DAYS_FULL[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate();

    if (dayEvents.length === 0) {
      body.innerHTML = '<div class="dtts-events__sidebar-empty">No events on this day.</div>';
      return;
    }

    body.innerHTML = dayEvents.map((ev) => {
      const slug = CATEGORY_SLUG(ev.category);
      return '' +
        '<div class="dtts-events__sidebar-event">' +
          '<div class="dtts-events__sidebar-title">' +
            escHTML(ev.title) +
          '</div>' +
          (ev.category ? '<div class="dtts-events__sidebar-category"><span class="dtts-events__category-dot dtts-events__category-dot--' + slug + '" style="background:' + getCategoryColor(ev.category) + '"></span> ' + escHTML(ev.category) + '</div>' : '') +
          '<div class="dtts-events__sidebar-meta">' +
            '<span>' + iconClock() + ' ' + escHTML(ev._timeStr) + (ev._endTimeStr ? ' – ' + escHTML(ev._endTimeStr) : '') + '</span>' +
            (ev.location ? '<span>' + iconPin() + ' ' + escHTML(ev.location) + '</span>' : '') +
          '</div>' +
          (ev.button && ev.button_link
            ? '<div class="dtts-events__sidebar-cta-row">' + eventCtaButtonHTML(ev) + '</div>'
            : '') +
          (ev.description_html
            ? '<div class="dtts-events__sidebar-desc">' + ev.description_html + '</div>'
            : (ev.description ? '<div class="dtts-events__sidebar-desc">' + escHTML(ev.description) + '</div>' : '')) +
          '<div class="dtts-events__sidebar-actions">' +
            (ev.details_url ? '<a href="' + ev.details_url + '" class="dtts-events__btn--details">Details</a>' : '') +
            '<button type="button" class="dtts-events__btn--share" data-sidebar-share="' + ev._index + '">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/></svg>' +
              ' Share' +
            '</button>' +
            calendarDropdownHTML(ev._index) +
          '</div>' +
        '</div>';
    }).join('');

    bindCalendarDropdowns(section, state);
    bindSidebarShare(section, state, body);
  }

  function bindSidebarShare(section, state, body) {
    body.querySelectorAll('[data-sidebar-share]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.dataset.sidebarShare, 10);
        var ev = state.events[idx];
        if (!ev) return;
        var shareData = {
          title: ev.title || '',
          text: ev.description || '',
          url: ev.details_url || window.location.href
        };
        if (navigator.share) {
          navigator.share(shareData).catch(function () {});
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareData.url).then(function () {
            var original = btn.innerHTML;
            btn.innerHTML = 'Link copied!';
            setTimeout(function () { btn.innerHTML = original; }, 1800);
          });
        } else {
          window.prompt('Copy this link', shareData.url);
        }
      });
    });
  }

  function clearSidebar(section) {
    const header = section.querySelector('.dtts-events__sidebar-header');
    const body = section.querySelector('.dtts-events__sidebar-body');
    if (header) header.textContent = 'Select a date';
    if (body) body.innerHTML = '<div class="dtts-events__sidebar-empty">Click a date to see events.</div>';
  }

  function renderLegend(section, state) {
    const legendEl = section.querySelector('.dtts-events__calendar-legend');
    if (!legendEl) return;

    const cats = getCategories(state.events);
    legendEl.innerHTML = Object.keys(cats).map((cat) => {
      const slug = CATEGORY_SLUG(cat);
      return '<span class="dtts-events__legend-item">' +
        '<span class="dtts-events__category-dot dtts-events__category-dot--' + slug + '" style="background:' + getCategoryColor(cat) + '"></span>' +
        escHTML(cat) + '</span>';
    }).join('');
  }

  /* ------------------------------------------------
     Add to Calendar (.ics download & Google Calendar)
     ------------------------------------------------ */
  function generateGoogleCalURL(ev) {
    if (!ev) return '#';
    var dtStart = toICSDate(ev._startDate);
    var dtEnd = ev._endDate ? toICSDate(ev._endDate) : dtStart;
    var params = [
      'action=TEMPLATE',
      'text=' + encodeURIComponent(ev.title || ''),
      'dates=' + dtStart + '/' + dtEnd,
      'details=' + encodeURIComponent(ev.description || ''),
      'location=' + encodeURIComponent(ev.location || '')
    ];
    return 'https://calendar.google.com/calendar/render?' + params.join('&');
  }

  function downloadICS(ev) {
    if (!ev) return;
    var dtStart = toICSDate(ev._startDate);
    var dtEnd = ev._endDate ? toICSDate(ev._endDate) : dtStart;

    var ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//DTTS//Events//EN',
      'BEGIN:VEVENT',
      'DTSTART:' + dtStart,
      'DTEND:' + dtEnd,
      'SUMMARY:' + (ev.title || ''),
      'DESCRIPTION:' + (ev.description || '').replace(/\n/g, '\\n'),
      'LOCATION:' + (ev.location || ''),
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    var blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = (ev.title || 'event').replace(/[^a-z0-9]/gi, '-').toLowerCase() + '.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function calendarDropdownHTML(evIndex) {
    return '' +
      '<div class="dtts-events__cal-dropdown">' +
        '<button class="dtts-events__btn--calendar" data-cal-toggle="' + evIndex + '">Add to Calendar</button>' +
        '<div class="dtts-events__cal-menu" data-cal-menu="' + evIndex + '">' +
          '<a href="#" class="dtts-events__cal-option" data-cal-google="' + evIndex + '"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg> Google Calendar</a>' +
          '<button class="dtts-events__cal-option" data-cal-ics="' + evIndex + '"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> iCalendar</button>' +
        '</div>' +
      '</div>';
  }

  function bindCalendarDropdowns(section, state) {
    section.querySelectorAll('[data-cal-toggle]:not([data-cal-bound])').forEach(function (btn) {
      btn.setAttribute('data-cal-bound', 'true');
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var idx = btn.dataset.calToggle;
        var menu = btn.closest('.dtts-events__cal-dropdown').querySelector('.dtts-events__cal-menu');
        /* Close all other menus */
        document.querySelectorAll('.dtts-events__cal-menu.is-open').forEach(function (m) {
          if (m !== menu) m.classList.remove('is-open');
        });
        if (menu) menu.classList.toggle('is-open');
      });
    });

    section.querySelectorAll('[data-cal-google]:not([data-cal-bound])').forEach(function (link) {
      link.setAttribute('data-cal-bound', 'true');
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(link.dataset.calGoogle, 10);
        var ev = state.events[idx];
        if (ev) window.open(generateGoogleCalURL(ev), '_blank');
        link.closest('.dtts-events__cal-menu').classList.remove('is-open');
      });
    });

    section.querySelectorAll('[data-cal-ics]:not([data-cal-bound])').forEach(function (btn) {
      btn.setAttribute('data-cal-bound', 'true');
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.dataset.calIcs, 10);
        downloadICS(state.events[idx]);
        btn.closest('.dtts-events__cal-menu').classList.remove('is-open');
      });
    });
  }

  /* Close dropdowns on click outside */
  document.addEventListener('click', function () {
    document.querySelectorAll('.dtts-events__cal-menu.is-open').forEach(function (m) {
      m.classList.remove('is-open');
    });
  });

  /* ------------------------------------------------
     Details Modal
     ------------------------------------------------ */
  var detailsModalEl = null;

  function ensureDetailsModal() {
    if (detailsModalEl) return detailsModalEl;
    detailsModalEl = document.createElement('div');
    detailsModalEl.className = 'dtts-events__modal-overlay';
    detailsModalEl.setAttribute('aria-hidden', 'true');
    detailsModalEl.innerHTML = '' +
      '<div class="dtts-events__modal" role="dialog" aria-modal="true" aria-labelledby="dtts-events-modal-title">' +
        '<button type="button" class="dtts-events__modal-close" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
        '</button>' +
        '<div class="dtts-events__modal-body"></div>' +
      '</div>';
    document.body.appendChild(detailsModalEl);

    detailsModalEl.addEventListener('click', function (e) {
      if (e.target === detailsModalEl) closeDetailsModal();
    });
    detailsModalEl.querySelector('.dtts-events__modal-close').addEventListener('click', closeDetailsModal);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && detailsModalEl && detailsModalEl.classList.contains('is-open')) {
        closeDetailsModal();
      }
    });
    return detailsModalEl;
  }

  function closeDetailsModal() {
    if (!detailsModalEl) return;
    detailsModalEl.classList.remove('is-open');
    detailsModalEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function openDetailsModal(ev) {
    if (!ev) return;
    var modal = ensureDetailsModal();
    var body = modal.querySelector('.dtts-events__modal-body');
    var d = ev._startDate;
    var dateStr = DAYS_FULL[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    var timeStr = ev._timeStr || '';
    if (ev._endTimeStr) timeStr += ' – ' + ev._endTimeStr;
    var slug = CATEGORY_SLUG(ev.category);

    body.innerHTML = '' +
      (ev.image ? '<div class="dtts-events__modal-image"><img src="' + escHTML(ev.image) + '" alt="' + escHTML(ev.title) + '" loading="lazy"' + imagePositionStyle(ev.image_position) + '></div>' : '') +
      '<h3 class="dtts-events__modal-title" id="dtts-events-modal-title">' + escHTML(ev.title) + '</h3>' +
      (ev.category ? '<div class="dtts-events__modal-category"><span class="dtts-events__category-dot dtts-events__category-dot--' + slug + '" style="background:' + getCategoryColor(ev.category) + '"></span> ' + escHTML(ev.category) + '</div>' : '') +
      '<div class="dtts-events__modal-time">' +
        '<span class="dtts-events__modal-meta-item">' + iconCalendar() + ' ' + escHTML(dateStr) + '</span>' +
        (timeStr ? '<span class="dtts-events__modal-meta-item">' + iconClock() + ' ' + escHTML(timeStr) + '</span>' : '') +
        (ev.location ? '<span class="dtts-events__modal-meta-item">' + iconPin() + ' ' + escHTML(ev.location) + '</span>' : '') +
      '</div>' +
      (ev.button && ev.button_link
        ? '<div class="dtts-events__modal-cta-row">' + eventCtaButtonHTML(ev) + '</div>'
        : '') +
      (ev.description_html
        ? '<div class="dtts-events__modal-desc">' + ev.description_html + '</div>'
        : (ev.description ? '<div class="dtts-events__modal-desc">' + escHTML(ev.description) + '</div>' : '')) +
      '<div class="dtts-events__modal-actions">' +
        '<button type="button" class="dtts-events__modal-share" data-modal-share>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/></svg>' +
          ' Share' +
        '</button>' +
        '<div class="dtts-events__modal-cal">' +
          '<button type="button" class="dtts-events__modal-cal-google" data-modal-google>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>' +
            ' Google Calendar' +
          '</button>' +
          '<button type="button" class="dtts-events__modal-cal-ics" data-modal-ics>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>' +
            ' iCalendar' +
          '</button>' +
        '</div>' +
      '</div>';

    body.querySelector('[data-modal-share]').addEventListener('click', function () {
      var shareData = {
        title: ev.title || '',
        text: ev.description || '',
        url: ev.details_url || window.location.href
      };
      if (navigator.share) {
        navigator.share(shareData).catch(function () {});
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareData.url).then(function () {
          var btn = body.querySelector('[data-modal-share]');
          var original = btn.innerHTML;
          btn.innerHTML = 'Link copied!';
          setTimeout(function () { btn.innerHTML = original; }, 1800);
        });
      } else {
        window.prompt('Copy this link', shareData.url);
      }
    });
    body.querySelector('[data-modal-google]').addEventListener('click', function () {
      window.open(generateGoogleCalURL(ev), '_blank');
    });
    body.querySelector('[data-modal-ics]').addEventListener('click', function () {
      downloadICS(ev);
    });

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function bindDetailsButtons(section, state) {
    section.querySelectorAll('[data-details-toggle]:not([data-details-bound])').forEach(function (btn) {
      btn.setAttribute('data-details-bound', 'true');
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var idx = parseInt(btn.dataset.detailsToggle, 10);
        var ev = state.events[idx];
        if (ev) openDetailsModal(ev);
      });
    });
  }

  /* ------------------------------------------------
     SVG Icon Helpers
     ------------------------------------------------ */
  function iconCalendar() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="16" height="16"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>';
  }

  function iconClock() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="14" height="14"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>';
  }

  function iconPin() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="14" height="14"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>';
  }

  /* ------------------------------------------------
     Utility
     ------------------------------------------------ */
  function imagePositionStyle(pos) {
    var p = String(pos || '').toLowerCase().trim();
    if (p !== 'top' && p !== 'bottom') p = 'center';
    return ' style="object-position: ' + p + ';"';
  }

  function escHTML(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /* escHTML escapes &<> but not quotes, so harden it for attribute values. */
  function escAttr(str) {
    return escHTML(str).replace(/"/g, '&quot;');
  }

  /* Per-event call-to-action button, shown only when both the label
     (button) and link (button_link) are set on the metaobject. Rendered
     before the Details button wherever event actions appear. */
  function eventCtaButtonHTML(ev) {
    if (!ev.button || !ev.button_link) return '';
    return '<a href="' + escAttr(ev.button_link) + '" class="dtts-events__btn--cta" target="_blank" rel="noopener noreferrer">' + escHTML(ev.button) + '</a>';
  }

  function truncateWords(str, maxWords) {
    if (!str) return '';
    var words = String(str).trim().split(/\s+/);
    if (words.length <= maxWords) return str;
    return words.slice(0, maxWords).join(' ') + '…';
  }

  /* ------------------------------------------------
     Boot
     ------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.dtts-events-section').forEach(function (section) {
      init(section);
    });
  });
})();
